/**
 * Drill Success Modal Component
 * Shows "Well Done!" message with Next Question button for drill mode
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Trophy, Sparkles, ArrowRight, Star, PartyPopper, Home } from "lucide-react";
import { GemBonusLine } from "./SuccessModal";

// Confetti colors matching our theme
const CONFETTI_COLORS = ["#0EA5E9", "#F97316", "#22C55E", "#FBBF24", "#A855F7"];

function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 8,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="confetti-container">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="confetti"
          initial={{ 
            x: `${particle.x}vw`, 
            y: -20, 
            rotate: 0,
            opacity: 1 
          }}
          animate={{ 
            y: "110vh", 
            rotate: 720,
            opacity: 0 
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            ease: "linear",
          }}
          style={{
            backgroundColor: particle.color,
            width: particle.size,
            height: particle.size,
            borderRadius: Math.random() > 0.5 ? "50%" : "2px",
          }}
        />
      ))}
    </div>
  );
}

export default function DrillSuccessModal({ 
  isOpen, 
  onNextQuestion,
  onReturnHome,
  pointsEarned = 10,
  blitzBonus = 0,
  streakBonus = 0,
  gemBonus = 0,
  streakCount = 0,
  isDrillComplete = false,
  questionsCompleted = 0,
  totalQuestions = 0,
  drillTitle = ""
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Confetti />
          <Dialog open={isOpen} onOpenChange={() => {}}>
            <DialogContent className="sm:max-w-md text-center [&>button]:hidden" data-testid="drill-success-modal">
              <DialogTitle className="sr-only">{isDrillComplete ? "Congratulations" : "Well Done"}</DialogTitle>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 20 
                }}
                className="py-6"
              >
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    delay: 0.2, 
                    type: "spring", 
                    stiffness: 400 
                  }}
                  className={`mx-auto mb-6 p-4 rounded-full w-fit ${
                    isDrillComplete ? "bg-green-500/10" : "bg-orange-500/10"
                  }`}
                >
                  {isDrillComplete ? (
                    <PartyPopper className="w-16 h-16 text-green-500" />
                  ) : (
                    <Trophy className="w-16 h-16 text-orange-500" />
                  )}
                </motion.div>

                {/* Title Text */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {isDrillComplete ? (
                    <>
                      <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2 flex items-center justify-center gap-2">
                        <Sparkles className="w-6 h-6 text-green-500" />
                        <span className="text-green-500">Congratulations!</span>
                        <Sparkles className="w-6 h-6 text-green-500" />
                      </h2>
                      <p className="text-muted-foreground mt-2">
                        You've completed the drill!
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        "{drillTitle}" - {totalQuestions}/{totalQuestions} questions
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2 flex items-center justify-center gap-2">
                        <Sparkles className="w-6 h-6 text-orange-500" />
                        <span className="text-orange-500">Well Done!</span>
                        <Sparkles className="w-6 h-6 text-orange-500" />
                      </h2>
                      <p className="text-muted-foreground mt-1">
                        Question {questionsCompleted} of {totalQuestions} complete
                      </p>
                    </>
                  )}
                  
                  {/* Points Display */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="mt-4 mb-2"
                  >
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 rounded-full">
                      <Star className="w-5 h-5 text-orange-500 fill-orange-500" />
                      <span className="text-lg font-bold text-orange-500">+{pointsEarned} points</span>
                    </div>
                    {blitzBonus > 0 && (
                      <p className="text-primary font-medium mt-2">
                        ⚡ Blitz Bonus +{blitzBonus} included!
                      </p>
                    )}
                    {streakBonus > 0 && (
                      <p className="text-green-500 font-medium mt-2">
                        🔥 Streak Bonus +{streakBonus} included!
                      </p>
                    )}
                    <GemBonusLine gemBonus={gemBonus} />
                    {streakCount >= 3 && (
                      <p className="text-orange-500 font-medium mt-2">
                        {streakCount} question streak! 🔥
                      </p>
                    )}
                  </motion.div>
                </motion.div>

                {/* Action Button */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8"
                >
                  {isDrillComplete ? (
                    <Button
                      onClick={onReturnHome}
                      className="gap-2 rounded-xl px-8 h-12 bg-green-500 hover:bg-green-600"
                      data-testid="return-home-btn"
                    >
                      <Home className="w-4 h-4" />
                      Return to Home
                    </Button>
                  ) : (
                    <Button
                      onClick={onNextQuestion}
                      className="gap-2 rounded-xl px-8 h-12 bg-orange-500 hover:bg-orange-600"
                      data-testid="next-question-btn"
                    >
                      Next Question
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  )}
                </motion.div>
              </motion.div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </AnimatePresence>
  );
}
